import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import ToastContainer from './ToastContainer';
import { TOAST_TYPES, DEFAULT_TOAST_OPTIONS } from './toastConstants';

const ToastContext = createContext(null);

// Re-export for convenience
export { TOAST_TYPES };

/**
 * Custom hook to access toast functionality
 * @returns {Object} Toast methods: success, error, warning, info, dismiss, dismissAll
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

/**
 * Toast Provider Component
 * Manages toast state and provides methods to show/dismiss toasts
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  /**
   * Add a new toast to the stack
   */
  const addToast = useCallback((type, message, options = {}) => {
    const id = ++toastIdRef.current;
    const toast = {
      id,
      type,
      message,
      ...DEFAULT_TOAST_OPTIONS,
      ...options,
      createdAt: Date.now(),
    };

    setToasts((prev) => [...prev, toast]);

    // Auto-dismiss if duration is set
    if (toast.duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, toast.duration);
    }

    return id;
  }, []);

  /**
   * Dismiss a specific toast by ID
   */
  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  /**
   * Dismiss all toasts
   */
  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  /**
   * Convenience methods for each toast type
   */
  const success = useCallback(
    (message, options) => addToast(TOAST_TYPES.SUCCESS, message, options),
    [addToast]
  );

  const error = useCallback(
    (message, options) =>
      addToast(TOAST_TYPES.ERROR, message, {
        duration: 8000, // Errors stay longer by default
        ...options,
      }),
    [addToast]
  );

  const warning = useCallback(
    (message, options) => addToast(TOAST_TYPES.WARNING, message, options),
    [addToast]
  );

  const info = useCallback(
    (message, options) => addToast(TOAST_TYPES.INFO, message, options),
    [addToast]
  );

  /**
   * Generic showToast method for backward compatibility
   * @param {string} message - The toast message
   * @param {string} type - The toast type: 'success', 'error', 'warning', 'info'
   * @param {Object} options - Optional toast options
   */
  const showToast = useCallback(
    (message, type = 'info', options) => {
      switch (type) {
        case 'success':
          return success(message, options);
        case 'error':
          return error(message, options);
        case 'warning':
          return warning(message, options);
        case 'info':
        default:
          return info(message, options);
      }
    },
    [success, error, warning, info]
  );

  const value = {
    toasts,
    success,
    error,
    warning,
    info,
    showToast,
    dismiss,
    dismissAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export default ToastContext;
